import { ERROR_MESSAGES, type ErrorCode } from '@en3/shared';

/**
 * 前端统一的 API 错误类型。
 *
 * `code` 与后端信封里的 code 一一对应（见 `@en3/shared` 的 errorCodes），
 * 额外增加一个仅前端存在的 `NETWORK_ERROR`：请求根本没到服务端（断网、超时、被拦截）时使用。
 * `message` 永远是可以直接展示的中文文案。
 */
export type ClientErrorCode = ErrorCode | 'NETWORK_ERROR';

export const NETWORK_ERROR_MESSAGE = '网络连接失败，请检查网络后重试';

export class ApiError extends Error {
  readonly code: ClientErrorCode;
  readonly status: number;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(input: {
    code: ClientErrorCode;
    status: number;
    message?: string;
    details?: unknown;
    requestId?: string;
  }) {
    super(input.message ?? defaultMessage(input.code));
    this.name = 'ApiError';
    this.code = input.code;
    this.status = input.status;
    if (input.details !== undefined) this.details = input.details;
    if (input.requestId !== undefined) this.requestId = input.requestId;
  }

  /** 需要重新登录（或刷新令牌）的失败。 */
  get isAuthError(): boolean {
    return (
      this.status === 401 ||
      this.code === 'AUTH_REQUIRED' ||
      this.code === 'TOKEN_EXPIRED' ||
      this.code === 'TOKEN_INVALID'
    );
  }

  /** 4xx：重试也不会变好。 */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /** zod 校验失败时后端会给 details.issues，这里取出来供表单逐字段标红。 */
  get fieldIssues(): { path: string; message: string }[] {
    const issues = (this.details as { issues?: unknown } | undefined)?.issues;
    if (!Array.isArray(issues)) return [];
    return issues
      .filter((item): item is { path?: unknown; message?: unknown } => typeof item === 'object' && item !== null)
      .map((item) => ({ path: String(item.path ?? ''), message: String(item.message ?? '') }));
  }
}

function defaultMessage(code: ClientErrorCode): string {
  if (code === 'NETWORK_ERROR') return NETWORK_ERROR_MESSAGE;
  return ERROR_MESSAGES[code] ?? ERROR_MESSAGES.INTERNAL;
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** 把任意异常转成可直接展示的中文文案。 */
export function toDisplayMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error) return error.message || ERROR_MESSAGES.INTERNAL;
  return ERROR_MESSAGES.INTERNAL;
}
